import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { Price } from "@modules/boxOffer/entities/boxOfferTypes"
import {
  CouponInputFormik,
  CouponInputFormikKeys,
  DurationType,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUText } from "@modules/ui/components/huText"
import { SimpleFormik } from "@src/typings/formik"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

type DiscountOfferFormProps = {
  formik: SimpleFormik<CouponInputFormik, CouponInputFormikKeys>
  price: Price
  isPending: boolean
  isDisableMonthsInput?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export function DiscountOfferForm({
  formik,
  price,
  isPending,
  isDisableMonthsInput,
  ...props
}: DiscountOfferFormProps): React.JSX.Element {
  const theme = useTheme()
  const language = useLanguage()
  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const defaultClassName = "flex flex-column w-full"

  const discountTypesOptions = useMemo(() => {
    return [
      { value: CouponType.PERCENT, label: word("box.member.table.offer.discount.percentInputLabel") },
      { value: CouponType.DIRECT, label: word("box.member.table.offer.discount.directInputLabel") },
    ]
  }, [language])

  const durationTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.discount.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.discount.unlimitedInputLabel") },
    ]
  }, [language])

  const priceDiscount = useMemo(() => {
    if (formik.values.value > 0 && price.value > 0) {
      let priceAfterDiscount = price.value
      if (formik.values.type === CouponType.PERCENT) {
        const discountPercentage = formik.values.value / 100
        priceAfterDiscount = priceAfterDiscount - priceAfterDiscount * discountPercentage
      } else if (formik.values.type === CouponType.DIRECT) {
        priceAfterDiscount = priceAfterDiscount - formik.values.value
      }
      return priceAfterDiscount
    } else return null
  }, [formik.values.value, formik.values.type])

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  return (
    <div className={props.className ?? defaultClassName} {...props}>
      <FormikInput
        flex
        formikStyle={{ flexGrow: 1 }}
        label={word("box.member.table.offer.discount.supensionTypeLabel")}
        type="dropdown"
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("type", formik)}
        name="type"
        error={getFormErrorMessage("type", formik)}
        dropdownProps={{
          value: formik.values.type,
          options: discountTypesOptions,
        }}
        setFieldValue={formik.setFieldValue}
        disabled={isPending}
      />
      <div className="flex flex-grow">
        <FormikInput
          flex
          formikStyle={{ flexGrow: 1 }}
          type="number"
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("value", formik)}
          name="value"
          error={getFormErrorMessage("value", formik)}
          numberProps={{
            min: 0,
            max: formik.values.type === CouponType.PERCENT ? 100 : price.value,
            step: 0.1,
            minFractionDigits: 0,
            maxFractionDigits: 2,
          }}
          setFieldValue={formik.setFieldValue}
          disabled={isPending}
        />
        {formik.values.type === CouponType.PERCENT && (
          <HUText
            fontStyle="TS"
            color={theme.neutralColor700}
            className="flex justify-content-center align-items-center ml-2 pb-3 "
          >
            %
          </HUText>
        )}
      </div>

      <div className="flex w-full justify-content-between mt-2 mb-4 px-4">
        <div className="flex flex-column align-items-center">
          <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
            {word("box.member.table.offer.discount.offerPriceLabel")}
          </HUText>
          <HUText
            fontStyle="TM"
            color={priceDiscount !== null && priceDiscount >= 0 ? theme.error : theme.neutralColor700}
            className={` mt-2`}
            style={
              priceDiscount !== null && priceDiscount >= 0
                ? { textDecorationThickness: "1px", textDecorationLine: "line-through" }
                : {}
            }
          >
            {formatCurrency(price.value, price.currency, language)}
          </HUText>
        </div>
        {priceDiscount !== null && priceDiscount >= 0 ? (
          <div className="flex align-items-end">
            <ArrowDrawIcon color={theme.neutralColor700} />
          </div>
        ) : null}
        <div className="flex flex-column align-items-center">
          <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
            {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
          </HUText>
          <HUText
            fontStyle="TM"
            color={priceDiscount !== null && priceDiscount >= 0 ? theme.sucess : theme.neutralColor700}
            className="mt-2"
          >
            {priceDiscount !== null && priceDiscount >= 0
              ? formatCurrency(priceDiscount, price.currency, language)
              : "--"}
          </HUText>
        </div>
      </div>
      {isDisableMonthsInput ? null : (
        <>
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            label={word("box.member.table.offer.discount.durationTypeLabel")}
            type="dropdown"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("durationType", formik)}
            name="durationType"
            error={getFormErrorMessage("durationType", formik)}
            dropdownProps={{
              value: formik.values.durationType,
              options: durationTypesOptions,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isPending}
          />
          {isDisableMonths ? null : (
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1 }}
              type="number"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("months", formik)}
              name="months"
              error={getFormErrorMessage("months", formik)}
              numberProps={{
                min: 1,
                maxFractionDigits: 0,
              }}
              setFieldValue={formik.setFieldValue}
              disabled={isDisableMonths || isPending}
            />
          )}
        </>
      )}
    </div>
  )
}
